/**
 * NotificationItem Component
 *
 * Displays a single notification with icon, content, and actions.
 */

import React from 'react';
import {
    Box,
    Typography,
    IconButton,
    Chip,
    alpha,
} from '@mui/material';
import {
    Close as CloseIcon,
    AttachMoney as CashIcon,
    CalendarMonth as CalendarIcon,
    ErrorOutline as AlertCircleIcon,
    Description as FileTextIcon,
    RemoveCircleOutline as FileMinusIcon,
    Logout as LogOutIcon,
    Build as ToolIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
    PersonAdd as UserPlusIcon,
    PersonRemove as UserMinusIcon,
    Edit as PenToolIcon,
    Timeline as ActivityIcon,
    Warning as AlertTriangleIcon,
    Notifications as BellIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

// Icon mapping based on notification icon field
const iconMap = {
    cash: CashIcon,
    calendar: CalendarIcon,
    'alert-circle': AlertCircleIcon,
    'file-text': FileTextIcon,
    'file-minus': FileMinusIcon,
    'log-out': LogOutIcon,
    tool: ToolIcon,
    'check-circle': CheckCircleIcon,
    info: InfoIcon,
    'user-plus': UserPlusIcon,
    'user-minus': UserMinusIcon,
    'pen-tool': PenToolIcon,
    activity: ActivityIcon,
    'alert-triangle': AlertTriangleIcon,
    bell: BellIcon,
};

// Category color mapping
const categoryColors = {
    PAYMENT: { bg: '#dcfce7', color: '#16a34a' },
    LEASE: { bg: '#dbeafe', color: '#2563eb' },
    MAINTENANCE: { bg: '#fef3c7', color: '#d97706' },
    TENANT: { bg: '#f3e8ff', color: '#9333ea' },
    AUDIT: { bg: '#fce7f3', color: '#db2777' },
    SYSTEM: { bg: '#f3f4f6', color: '#6b7280' },
};

// Priority border colors
const priorityColors = {
    CRITICAL: '#ef4444',
    HIGH: '#f59e0b',
    NORMAL: 'transparent',
    LOW: 'transparent',
};

const NotificationItem = ({ notification, onMarkAsRead, onDelete, onClick }) => {
    const {
        notification_id,
        title,
        message,
        category,
        priority,
        icon,
        is_read,
        action_url,
        created_at,
    } = notification;

    const IconComponent = iconMap[icon] || BellIcon;
    const categoryStyle = categoryColors[category] || categoryColors.SYSTEM;
    const borderColor = priorityColors[priority] || 'transparent';

    const handleClick = () => {
        if (!is_read && onMarkAsRead) {
            onMarkAsRead(notification_id);
        }
        if (onClick) {
            onClick(notification, action_url);
        }
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        if (onDelete) {
            onDelete(notification_id);
        }
    };

    const formatTime = (dateString) => {
        try {
            return formatDistanceToNow(new Date(dateString), { addSuffix: true });
        } catch {
            return '';
        }
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                p: 1.5,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
                borderLeft: borderColor !== 'transparent' ? `3px solid ${borderColor}` : 'none',
                bgcolor: is_read ? 'transparent' : alpha('#3b82f6', 0.05),
                '&:hover': {
                    bgcolor: is_read ? alpha('#000', 0.02) : alpha('#3b82f6', 0.1),
                },
            }}
        >
            {/* Icon */}
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    flexShrink: 0,
                    bgcolor: categoryStyle.bg,
                    color: categoryStyle.color,
                }}
            >
                <IconComponent sx={{ fontSize: 20 }} />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: is_read ? 500 : 600,
                        color: '#111827',
                        mb: 0.25,
                        lineHeight: 1.3,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: '#6b7280',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        fontSize: '0.8125rem',
                    }}
                >
                    {message}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography
                        variant="caption"
                        sx={{ color: '#9ca3af', fontSize: '0.75rem' }}
                    >
                        {formatTime(created_at)}
                    </Typography>
                    <Chip
                        label={category}
                        size="small"
                        sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 500,
                            bgcolor: categoryStyle.bg,
                            color: categoryStyle.color,
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                {!is_read && (
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#3b82f6',
                        }}
                        title="Unread"
                    />
                )}
                <IconButton
                    size="small"
                    onClick={handleDelete}
                    sx={{
                        opacity: 0.5,
                        '&:hover': { opacity: 1 },
                    }}
                    title="Dismiss"
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
        </Box>
    );
};

export default NotificationItem;
